import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { cardBackground, cardBorder } from '../styles/themeHelpers';
import { completePasswordReset } from '../services/passwordResetService';
import PasswordField from '../components/common/PasswordField';
import AnimatedBackground from '../components/common/AnimatedBackground';

const Background = styled.div`
  min-height: 100vh;
  background: ${({ theme }) =>
    theme.profile?.pageBackground === 'transparent'
      ? 'transparent'
      : theme.gradients?.background || theme.colors.background};
  position: relative;
  overflow: hidden;
`;

const CenterBlock = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
`;

const AccessFrame = styled.div`
  background: ${({ theme }) => cardBackground(theme)};
  border: 2px solid ${({ theme }) => cardBorder(theme)};
  box-shadow: 0 0 30px rgba(0, 212, 255, 0.3);
  padding: 30px;
  border-radius: 12px;
  max-width: 420px;
  width: 100%;
  position: relative;
  text-align: center;
  z-index: 10;
  backdrop-filter: none;

  @media (max-width: 768px) {
    padding: 25px 20px;
  }
`;

const AccessTitle = styled.h1`
  font-size: 1.5rem;
  color: #00d4ff;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 10px;
  font-weight: bold;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
`;

const SubStatus = styled.div`
  color: #b0b0b0;
  font-size: 13px;
  margin-bottom: 25px;
  letter-spacing: 1px;
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 20px;
`;

const StepDot = styled.div<{ $active?: boolean; $completed?: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props =>
    props.$completed ? '#4caf50' :
    props.$active ? '#00d4ff' :
    'rgba(255, 255, 255, 0.2)'};
  transition: all 0.3s ease;
`;

const InputBlock = styled.div`
  margin-bottom: 15px;
`;

const SubmitButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #00d4ff, #0099cc);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 14px;
  font-weight: bold;
  text-transform: uppercase;
  padding: 12px 0;
  margin: 20px 0;
  cursor: pointer;
  letter-spacing: 1px;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #00a8cc, #007799);
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 212, 255, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const LinkRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 15px;
`;

const FrameLink = styled(Link)`
  color: #00d4ff;
  font-size: 12px;
  text-decoration: none;
  letter-spacing: 1px;
  padding: 6px 10px;
  border: 1px solid transparent;
  border-radius: 4px;
  transition: all 0.3s ease;

  &:hover {
    color: #fff;
    border-color: rgba(0, 212, 255, 0.5);
    background: rgba(0, 212, 255, 0.1);
  }
`;

const ErrorMsg = styled.div`
  color: #ff4444;
  font-size: 13px;
  text-align: center;
  margin-bottom: 15px;
  padding: 10px;
  background: rgba(255, 68, 68, 0.1);
  border: 1px solid rgba(255, 68, 68, 0.3);
  border-radius: 6px;
`;

const SuccessMsg = styled.div`
  color: #4caf50;
  font-size: 14px;
  text-align: center;
  margin-bottom: 20px;
  padding: 15px;
  border: 1px solid rgba(76, 175, 80, 0.3);
  background: rgba(76, 175, 80, 0.1);
  border-radius: 8px;
`;

const ResetPasswordNewPage: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordValid, setPasswordValid] = useState(false);
  const [email, setEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const storedEmail = sessionStorage.getItem('resetEmail');
    const storedCode = sessionStorage.getItem('resetCode');

    if (!storedEmail || !storedCode) {
      navigate('/forgot-password');
      return;
    }

    setEmail(storedEmail);
    setResetCode(storedCode);
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!passwordValid) {
      setError('Password does not meet requirements or passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await completePasswordReset(email, resetCode, password, confirmPassword);

      if (response.success) {
        setSuccess(true);
        sessionStorage.removeItem('resetEmail');
        sessionStorage.removeItem('resetCode');

        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Error setting new password. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Background>
      <AnimatedBackground />

      <CenterBlock>
        <AccessFrame>
          <AccessTitle>New Password</AccessTitle>
          <SubStatus>Step 3 of 3</SubStatus>

          <StepIndicator>
            <StepDot $completed />
            <StepDot $completed />
            <StepDot $active />
          </StepIndicator>

          {success ? (
            <SuccessMsg>
              Password successfully changed!
              <br />
              <span style={{ fontSize: '12px', marginTop: '10px', display: 'block' }}>
                Redirecting to login page...
              </span>
            </SuccessMsg>
          ) : (
            <form onSubmit={handleSubmit} autoComplete="off">
              {error && <ErrorMsg>{error}</ErrorMsg>}

              <InputBlock>
                <PasswordField
                  label="New Password"
                  value={password}
                  onChange={setPassword}
                  withConfirm
                  confirmValue={confirmPassword}
                  onConfirmChange={setConfirmPassword}
                  autoComplete="new-password"
                  onValidityChange={setPasswordValid}
                  showInlineChecklist={false}
                  showHelp
                />
              </InputBlock>

              <SubmitButton
                type="submit"
                disabled={loading || !password || !confirmPassword}
              >
                {loading ? 'Saving...' : 'Save New Password'}
              </SubmitButton>
            </form>
          )}

          <LinkRow>
            <FrameLink to="/login">Back to Login</FrameLink>
          </LinkRow>
        </AccessFrame>
      </CenterBlock>
    </Background>
  );
};

export default ResetPasswordNewPage;
